import { Co2MaterialType, Co2MaterialTypeByCategory, Co2MaterialTypeOptions, Co2MaterialTypesClient, UpsertCo2MaterialTypeRequest, UpsertCo2MaterialTypeResponse } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

const co2MaterialTypesClient = new Co2MaterialTypesClient(undefined, axios);

export type Co2MaterialTypeQueryFilter = {
  field: string;
  operator: string;
  value?: string;
  values?: string[];
};

export type Co2MaterialTypeQueryRequest = {
  materialLibraryIds?: string[];
  showArchived?: boolean;
  search?: string;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  filters?: Co2MaterialTypeQueryFilter[];
};

export type Co2MaterialTypeQueryResponse = {
  items: Co2MaterialType[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export const useCo2MaterialTypes = (materialLibraryIds: string[] | undefined = undefined, showArchived: boolean = false, options?: any) => {
  return useQuery<Co2MaterialType[]>({
    ...options,
    queryKey: ["materialTypes", materialLibraryIds?.join(","), showArchived],
    queryFn: async () => co2MaterialTypesClient.getMaterialTypes(materialLibraryIds, showArchived),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes
  });
};

export const useCo2MaterialTypesQuery = (request: Co2MaterialTypeQueryRequest, options?: any) => {
  const { placeholderData, ...restOptions } = options ?? {};
  return useQuery<Co2MaterialTypeQueryResponse>({
    queryKey: ["materialTypesQuery", request],
    queryFn: async () => {
      const response = await axios.post("/api/co2-material-types/query", request);
      const data = response.data;
      return {
        ...data,
        items: (data.items ?? []).map((item: any) => Co2MaterialType.fromJS(item)),
      };
    },
    placeholderData: placeholderData ?? ((prevData: Co2MaterialTypeQueryResponse | undefined) => prevData),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    ...restOptions,
  });
};

// Server clamps pageSize to this value in Co2MaterialService.QueryCo2MaterialTypes,
// so the by-ids fetch must chunk requests above this size to avoid silent truncation.
const CO2_MATERIAL_TYPES_QUERY_MAX_PAGE_SIZE = 500;

/** Normalize GUID keys so node-material IDs and query results always match. */
export function normalizeCo2MaterialTypeId(id: string | undefined | null): string | undefined {
  if (!id) return undefined;
  return String(id).toLowerCase();
}

/** Parse API payloads from /query, tolerating camelCase and PascalCase property names. */
export function parseCo2MaterialTypeItem(item: unknown): Co2MaterialType {
  const raw = (item ?? {}) as Record<string, unknown>;
  const material = Co2MaterialType.fromJS(raw);

  if (!material.co2MaterialTypeId) {
    material.co2MaterialTypeId = (raw.co2MaterialTypeId ?? raw.Co2MaterialTypeId) as string | undefined;
  }
  if (material.name == null) {
    material.name = (raw.name ?? raw.Name) as string | undefined;
  }
  if (material.unit == null) {
    material.unit = (raw.unit ?? raw.Unit) as string | undefined;
  }
  if (material.a1A2A3 == null) {
    material.a1A2A3 = (raw.a1A2A3 ?? raw.A1A2A3) as number | undefined;
  }
  if (material.source == null) {
    material.source = (raw.source ?? raw.Source) as string | undefined;
  }

  return material;
}

export const useCo2MaterialTypesByIds = (
  ids: string[] | undefined,
  options?: { enabled?: boolean; scenarioId?: string }
) => {
  const normalizedIds = Array.from(new Set((ids ?? []).filter(Boolean)));
  const scenarioId = options?.scenarioId ?? "";
  return useQuery<Co2MaterialType[]>({
    queryKey: ["materialTypesByIds", scenarioId, normalizedIds.join(",")],
    enabled: normalizedIds.length > 0 && (options?.enabled ?? true),
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < normalizedIds.length; i += CO2_MATERIAL_TYPES_QUERY_MAX_PAGE_SIZE) {
        chunks.push(normalizedIds.slice(i, i + CO2_MATERIAL_TYPES_QUERY_MAX_PAGE_SIZE));
      }
      const responses = await Promise.all(
        chunks.map((chunk) =>
          axios.post<Co2MaterialTypeQueryResponse>("/api/co2-material-types/query", {
            page: 0,
            pageSize: chunk.length,
            // Server returns only Id, Name, Unit, DisplayUnit, A1A2A3, Source — the fields
            // every current by-ids consumer reads. Trims the response by ~10x and skips
            // SourceData LOB + library/category i18n joins.
            fields: "lite",
            filters: [{ field: "co2MaterialTypeId", operator: "isAnyOf", values: chunk }],
          })
        )
      );
      return responses.flatMap((response) =>
        (response.data.items ?? []).map((item: unknown) => parseCo2MaterialTypeItem(item))
      );
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useCo2MaterialTypeOptions = (materialLibraryIds: string[] | undefined = undefined, showArchived: boolean = false, options?: any) => {
  return useQuery<Co2MaterialTypeOptions>({
    ...options,
    queryKey: ["materialTypeOptions", materialLibraryIds?.join(","), showArchived],
    queryFn: async () => co2MaterialTypesClient.getMaterialTypeOptions(materialLibraryIds, showArchived),
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes
  });
};

export const useUpsertCo2MaterialType = (options?: any) => {
  return useMutation<UpsertCo2MaterialTypeRequest[]>({
    ...options,
    mutationFn: (upsertCo2MaterialTypeRequests: UpsertCo2MaterialTypeRequest[]) => co2MaterialTypesClient.upsertMaterialTypes(upsertCo2MaterialTypeRequests),
    onSuccess: async (data: UpsertCo2MaterialTypeResponse) => {
      if (data.failureItems?.length === 0) {
        await queryClient.resetQueries({ queryKey: ["materialLibraries"], exact: false });
        await queryClient.resetQueries({ queryKey: ["materialTypes"], exact: false });
        await queryClient.resetQueries({ queryKey: ["materialTypesQuery"], exact: false });
        await queryClient.resetQueries({ queryKey: ["materialTypeOptions"], exact: false });
      }

      options?.onSuccess?.(data);
    }
  });
};

export const useDeleteCo2MaterialType = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (co2MaterialTypeId: string) => co2MaterialTypesClient.deleteMaterialType(co2MaterialTypeId),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["materialTypes"], exact: false });
      await queryClient.resetQueries({ queryKey: ["materialTypesQuery"], exact: false });
      await queryClient.resetQueries({ queryKey: ["materialTypeOptions"], exact: false });

      options.onSuccess?.();
    }
  });
};

export const useCo2MaterialTypeByCategory = (options?: any) => {
  return useQuery<Co2MaterialTypeByCategory[]>({
    ...options,
    queryKey: ["co2-material-type-by-category"],
    queryFn: async () => co2MaterialTypesClient.co2MaterialTypeByCategory(),
  });
};